import { type FactorCategoryKey, type FactorKey, type ImpactLeafFactorKey, LEAF_SUM_KEYS, SUB_FACTOR_ORDER } from "consts";

import type { ContributionMarginBreakdown, ContributionMarginYearBreakdown } from "models";

import type { ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";

/** Rows from Soil Sight CM outcome (year slice); `year` + `contributionMargin` (€/ha) required; `adjustedCropMetrics` (t/ha) only needed for yield view. */
export type ImpactComputationYearRow = Pick<ContributionMarginYearBreakdown, "year" | "contributionMargin"> &
  Partial<Pick<ContributionMarginYearBreakdown, "adjustedCropMetrics">>;

/** Leaf keys keep their own vocabulary rather than mirroring the API's silver-model names: they are bound to
 *  Crowdin-managed i18n keys and to `data-testid`s, so renaming them churns every locale file. */
export function leafFromBreakdown(b?: ContributionMarginBreakdown | null): Record<ImpactLeafFactorKey, number> {
  return {
    regular_projection: bandAvg(b?.regularProjection),
    market_price_trend: bandAvg(b?.marketPriceTrend),
    input_cost_trend: bandAvg(b?.marketCostTrend),
    yield_amount: bandAvg(b?.yieldAndQuality?.cropYield),
    quality_improvement: bandAvg(b?.yieldAndQuality?.qualityImprovement),
    pest_pressure: bandAvg(b?.costSavings?.pestLossAvoided),
    nitrogen: bandAvg(b?.costSavings?.nResilience),
    leaching: bandAvg(b?.resilience?.leachingReduction),
    runoff: bandAvg(b?.resilience?.runoffReduction),
    erosion: bandAvg(b?.resilience?.erosionReduction),
    water_holding: bandAvg(b?.resilience?.soilAwcImprovement),
  };
}

/** Zero for every leaf key; spread when only a subset carries data. */
const zeroLeaf = (): Record<ImpactLeafFactorKey, number> => Object.fromEntries(LEAF_SUM_KEYS.map((k) => [k, 0])) as Record<ImpactLeafFactorKey, number>;

/** Yield view leaves are t/ha (per-year stock) from `adjustedCropMetrics.breakdown`, not the €/ha CM breakdown. Only 2 keys carry tonnage. */
function leafFromTonnes(row?: ImpactComputationYearRow | null): Record<ImpactLeafFactorKey, number> {
  const b = row?.adjustedCropMetrics?.breakdown;
  return {
    ...zeroLeaf(),
    regular_projection: b?.regularProjection ?? 0,
    yield_amount: b?.cropYieldImprovement ?? 0,
  };
}

/** Per milestone: yearly = single API year row; cumulative = sum of API years 1..milestone; yield = single year t/ha (no summing). */
export function computeImpactLeafTotals(years: ImpactComputationYearRow[], milestone: number, mode: ViewMode): Record<ImpactLeafFactorKey, number> {
  const out = zeroLeaf();

  // Single-year modes: pick the matching year row and read its leaves (t/ha for yield, €/ha for annual).
  if (mode === "yield" || mode === "annual") {
    const row = years.find((y) => y.year === milestone);
    if (!row) return out;
    const leaf = mode === "yield" ? leafFromTonnes(row) : leafFromBreakdown(row.contributionMargin?.breakdown);
    LEAF_SUM_KEYS.forEach((k) => {
      out[k] = leaf[k];
    });
    return out;
  }

  years
    .filter((y) => y.year > 0 && y.year <= milestone)
    .forEach((y) => {
      const leaf = leafFromBreakdown(y.contributionMargin?.breakdown);
      LEAF_SUM_KEYS.forEach((k) => {
        out[k] += leaf[k];
      });
    });

  return out;
}

// Resilience leaves the API may omit (irrigation model, or CM breakdown flag on).
const OPTIONAL_RESILIENCE_LEAVES: Partial<Record<FactorKey, keyof ContributionMarginBreakdown["resilience"]>> = {
  erosion: "erosionReduction",
  water_holding: "soilAwcImprovement",
};

export const reportsImpactFactor = (years: ImpactComputationYearRow[] | null | undefined, factorKey: FactorKey): boolean => {
  const leaf = OPTIONAL_RESILIENCE_LEAVES[factorKey];
  return !leaf || !!years?.some((year) => year.contributionMargin?.breakdown?.resilience?.[leaf] !== undefined);
};

export function categoryTotal(leaf: Record<ImpactLeafFactorKey, number>, cat: FactorCategoryKey): number {
  const keys = SUB_FACTOR_ORDER[cat];
  return keys.reduce((acc, key) => acc + (leaf[key] ?? 0), 0);
}
